/**
 * Small pieces of the lead-label view: a label badge with its reason, the
 * "right / wrong" marker a team correction gives each side, and the
 * correction control (Quotable / Not quotable / Pending, optional note).
 *
 * Corrections are stored in Core only. Nothing here sends anything to
 * WhatConverts or to Google Ads, and the copy never says otherwise.
 */
import { useState, type KeyboardEvent, type MouseEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  postLeadCorrection,
  type WcLabel,
  type WcLead,
  type WcLeadLabel,
  type WcQuotable,
} from '@/services/whatconvertsService';

import { LABEL_BADGE, LABEL_TEXT, matchesTeam } from './leadLabels';

/** Right or wrong against the team's correction; nothing when there is none. */
export function TeamMarker({ lead, answer }: { lead: WcLead; answer: WcQuotable | WcLabel | null | undefined }) {
  const right = matchesTeam(lead, answer);
  if (right === null) return null;
  return right ? (
    <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-teal-dark" data-testid="team-right">
      <Check className="h-3 w-3" aria-hidden="true" />
      Right
    </span>
  ) : (
    <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-coral-dark" data-testid="team-wrong">
      <X className="h-3 w-3" aria-hidden="true" />
      Wrong
    </span>
  );
}

/** One labeler's answer for a lead: badge, short reason, and the marker. */
export function LabelCell({ lead, label }: { lead: WcLead; label: WcLeadLabel | null }) {
  if (!label) {
    return <span className="text-xs text-muted-foreground">Not labelled yet</span>;
  }
  return (
    <div className="min-w-0 space-y-1">
      <div className="flex items-center gap-1.5">
        <Badge variant="outline" className={LABEL_BADGE[label.label]}>
          {LABEL_TEXT[label.label]}
        </Badge>
        <TeamMarker lead={lead} answer={label.label} />
      </div>
      {label.reason ? (
        <p className="line-clamp-2 max-w-[260px] text-xs text-muted-foreground" title={label.reason}>
          {label.reason}
        </p>
      ) : null}
    </div>
  );
}

const CHOICES: WcLabel[] = ['yes', 'no', 'pending'];

export interface CorrectionControlProps {
  lead: WcLead;
  tenantId: string;
  /** Row mode: smaller buttons, note field only after a pick. */
  compact?: boolean;
}

/**
 * The team's answer for a lead. Picking one opens a short optional note and
 * a Save button; saving appends a correction (the newest one is shown).
 * Clicks and keys stop here so a table row does not open its drawer.
 */
export function CorrectionControl({ lead, tenantId, compact }: CorrectionControlProps) {
  const qc = useQueryClient();
  const current = lead.correction?.label ?? null;
  const [picked, setPicked] = useState<WcLabel | null>(null);
  const [note, setNote] = useState('');

  const save = useMutation({
    mutationFn: (label: WcLabel) =>
      postLeadCorrection({
        tenantId,
        wcLeadId: lead.lead_id,
        label,
        note: note.trim() || undefined,
        dateCreated: lead.date_created,
      }),
    onSuccess: () => {
      setPicked(null);
      setNote('');
      void qc.invalidateQueries({ queryKey: ['whatconverts-leads', tenantId] });
      void qc.invalidateQueries({ queryKey: ['wc-lead-label-stats', tenantId] });
      void qc.invalidateQueries({ queryKey: ['wc-lead-label-detail', tenantId, String(lead.lead_id)] });
    },
  });

  const stop = (e: MouseEvent | KeyboardEvent) => e.stopPropagation();
  const size = compact ? 'h-6 px-1.5 text-[11px]' : 'h-8 px-2.5 text-xs';

  return (
    <div
      className="space-y-1.5"
      onClick={stop}
      onKeyDown={stop}
      role="group"
      aria-label={`Team check for lead ${lead.contact_name || lead.lead_id}`}
    >
      <div className="flex flex-wrap gap-1">
        {CHOICES.map((c) => {
          const on = (picked ?? current) === c;
          return (
            <Button
              key={c}
              type="button"
              variant="outline"
              aria-pressed={on}
              disabled={save.isPending}
              onClick={() => setPicked(c)}
              className={`${size} ${on ? LABEL_BADGE[c] : ''}`}
            >
              {LABEL_TEXT[c]}
            </Button>
          );
        })}
      </div>
      {picked ? (
        <form
          className="flex flex-wrap items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate(picked);
          }}
        >
          <Input
            aria-label="Note (optional)"
            placeholder="Note (optional)"
            value={note}
            maxLength={500}
            onChange={(e) => setNote(e.target.value)}
            className={compact ? 'h-6 w-40 text-xs' : 'h-8 w-56 text-sm'}
          />
          <Button type="submit" disabled={save.isPending} className={size}>
            {save.isPending ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" /> : null}
            Save
          </Button>
          <Button
            type="button"
            variant="ghost"
            className={size}
            onClick={() => {
              setPicked(null);
              setNote('');
              save.reset();
            }}
          >
            Cancel
          </Button>
        </form>
      ) : null}
      {save.isError ? (
        <p className="text-xs text-coral-dark" role="alert">
          Could not save the correction. Nothing was changed; try again.
        </p>
      ) : null}
    </div>
  );
}
